import React from 'react'
import classnames from 'classnames'
import { compose } from 'redux'
import {
  makeForm,
  RadioFormField,
  BaseComponent,
  TextFormField,
  MaskedFormField,
  SelectDropdownFormField
} from 'belle-js'
import styles from './TrustDetails.module.scss'
import withFormResponseHandler from '../../withFormResponseHandler'
import {
  DATE,
  MASK_CHAR_NUMBER,
  ID_TYPE
} from 'utils/fieldMasking'
import {
  trustDateValidation,
  stateValidation,
  trustNameValidation,
  identificationNumberValidation,
  firstNameValidation,
  lastNameValidation
} from 'utils/fieldValidations'

const getFormattedRadioLabel = texts => (
  <div className={styles.enrollmentOptionParent}>
    { texts.map((text, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <p key={index}>{text}</p>
    ))}
  </div>
)

const paperlessEnrollmentTypes = [{
  label: getFormattedRadioLabel(['Yes', 'Paperless, please!']),
  value: 'Yes',
}, {
  label: getFormattedRadioLabel(['No', 'Please send all documents via postal mail']),
  value: 'No',
}]

const regulatoryQuestionTypes = [{
  label: getFormattedRadioLabel(['Yes']),
  value: 'Yes',
}, {
  label: getFormattedRadioLabel(['No']),
  value: 'No',
}]

class TrustDetailsForm extends BaseComponent {

  constructor (props) {
    super(props)
    this.state = {
      wasIDEdited: false,
    }

    this.bind(
      'handleClick',
      'handleBlur',
      'handleFocus',
      'getMaskedValue'
    )
  }

  handleBlur (type) {
    const { formData } = this.props
    let newState = {}

    if (type === ID_TYPE && formData.taxIdentificationNumber === '') {
      newState = {
        wasIDEdited: false
      }
    }
    this.setState(newState)
  }

  handleFocus (type) {
    let newState = {}

    if (type === ID_TYPE) {
      newState = {
        wasIDEdited: true
      }
    }
    this.setState(newState)
  }

  getMaskedValue (value) {
    return value !== null ? value.padStart(value.length + MASK_CHAR_NUMBER, '*') : ''
  }

  handleClick () {
    const { openModal } = this.props
    openModal('paperlessInfoModal')
  }

  render () {
    const {
      formValid,
      onClick,
      initialData,
      accountSubType,
      irrevocableTrustType
    } = this.props

    const irrevocableTrustTypes = [
      { key: 'GRAT', label: 'GRAT', value: 'GRAT' },
      { key: 'CRUT', label: 'CRUT', value: 'CRUT' },
      { key: 'CRAT', label: 'CRAT', value: 'CRAT' },
      { key: 'GST', label: 'GST', value: 'GST' },
    ]

    const primaryButtonClasses = classnames(
      'button',
      'button--primary',
      styles.submitButton,
      !formValid ? 'disabled' : null
    )

    const paperLessQuestion = 'Would you like to enroll this account in Paperless Documents?'
    const regulatoryQuestion = 'Is the Trust a 10% shareholder of a publicly held company?'

    const {
      trustName,
      dateOfTrust,
      trustGovernedInStateOf,
      publicCompanyAffiliated,
      lastFourDigitTaxId,
      paperlessDelivery
    } = initialData

    const isIdentificationMasked = !this.state.wasIDEdited && lastFourDigitTaxId

    const isIrrevocableTrustAccount = accountSubType === 'Irrevocable Trust'

    const isTestamentaryTrustAccount = accountSubType === 'Testamentary Trust'

    return (
      <div className={styles.trustAccountDetails}>
        <div className={styles.section}>
          <div className={styles.formInput}>
            <p className={styles.heading}>Trust Type</p>
            <TextFormField
              name="typeOfAccount"
              label="trust type"
              type="text"
              placeholder={accountSubType}
              value={accountSubType}
              readOnly={true}
              className={styles.accountTypeFormField}
              data-test-id="typeOfAccount" />
          </div>
        </div>

        { isTestamentaryTrustAccount &&
          <div className={styles.section}>
            <div className={styles.testamentarySection}>
              <div className={styles.formInput}>
                <p className={styles.heading}>Name of Decedent</p>
                <TextFormField
                  name="decedentFirstName"
                  label="Decedent First Name"
                  type="text"
                  placeholder="First Name"
                  validations={firstNameValidation.rules}
                  validationErrors={firstNameValidation.messages}
                  value={initialData.decedentFirstName || ''}
                  className={styles.formField}
                  data-test-id="decedentFirstName" />
              </div>
            </div>

            <div className={styles.testamentarySection}>
              <div className={styles.formInput}>
                <TextFormField
                  name="decedentLastName"
                  label="Decedent Last Name"
                  type="text"
                  placeholder="Last Name"
                  validations={lastNameValidation.rules}
                  validationErrors={lastNameValidation.messages}
                  value={initialData.decedentLastName  || ''}
                  className={styles.formField}
                  data-test-id="decedentLastName" />
              </div>
            </div>
          </div>
        }

        { isIrrevocableTrustAccount &&
          <div className={styles.section}>
            <div className={styles.formInput}>
              <p className={styles.heading}>Select Type</p>
              <SelectDropdownFormField
                name="irrevocableTrustType"
                options={irrevocableTrustTypes}
                value={irrevocableTrustType}
                required={false}
                testId="irrevocableTrustType" />
            </div>
          </div>
        }

        <div className={styles.twoColumnSection}>
          <div className={styles.formInput}>
            <p className={styles.heading}>Trust Name</p>
            <TextFormField
              name="trustName"
              label="Trust Name"
              placeholder="Trust Name"
              type="text"
              validations={trustNameValidation.rules}
              validationErrors={trustNameValidation.messages}
              value={trustName || ''}
              className={styles.formField}
              data-test-id="accountHolderFullName" />
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.formInput}>
            <p className={styles.heading}>Date of Trust</p>
            <MaskedFormField
              name="dateOfTrust"
              type="text"
              placeholder="MM/DD/YYYY"
              mask={DATE}
              validations={trustDateValidation.rules}
              validationErrors={trustDateValidation.messages}
              value={dateOfTrust || ''}
              className={styles.accountTypeFormField}
              data-test-id="dateOfTrust" />
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.formInput}>
            <p className={styles.heading}>TRUST TAX ID NUMBER</p>
            <TextFormField
              name="taxIdentificationNumber"
              label="TRUST TAX ID NUMBER"
              type="text"
              placeholder="12-3456789"
              maxLength={32}
              onBlur={isIdentificationMasked ?  undefined : this.handleBlur.bind(this, ID_TYPE)} // eslint-disable-line
              onFocus={isIdentificationMasked ? this.handleFocus.bind(this, ID_TYPE) : undefined} // eslint-disable-line
              validations={isIdentificationMasked ?
                undefined : identificationNumberValidation.rules}
              validationErrors={
                isIdentificationMasked ? undefined : identificationNumberValidation.messages}
              value={
                isIdentificationMasked ? this.getMaskedValue(lastFourDigitTaxId) : ''
              }
              required={false}
              className={styles.accountTypeFormField}
              data-test-id="taxIdentificationNumber" />
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.formInput}>
            <p className={styles.heading}>GOVERNED BY STATE LAWS OF</p>
            <TextFormField
              name="trustGovernedInStateOf"
              label="GOVERNED BY STATE LAWS OF"
              placeholder="State"
              type="text"
              maxLength={32}
              validations={stateValidation.rules}
              validationErrors={stateValidation.messages}
              value={trustGovernedInStateOf || ''}
              className={styles.accountTypeFormField}
              data-test-id="trustGovernedInStateOf"  />
          </div>
        </div>

        <div className={styles.paperlessDocumentEnrollment}>
          <p className={styles.heading}>REGULATORY QUESTION</p>
          <span className={styles.question}>
            {regulatoryQuestion}
          </span>
          <div>
            <RadioFormField
              className={styles.paperlessRadio}
              name="publicCompanyAffiliated"
              horizontal={false}
              block={false}
              options={regulatoryQuestionTypes}
              value={publicCompanyAffiliated || ''}
              data-test-id="publicCompanyAffiliated" />
          </div>
        </div>

        <div className={styles.paperlessDocumentEnrollment}>
          <p className={styles.heading}>Paperless Document Enrollment</p>
          <span className={styles.question}>
            {paperLessQuestion}
            <a className={styles.moreSection} onClick={this.handleClick}> MORE </a>
          </span>
          <div>
            <RadioFormField
              label="Paperless Enrollment"
              className={styles.paperlessRadio}
              name="paperlessDelivery"
              horizontal={false}
              block={false}
              options={paperlessEnrollmentTypes}
              value={paperlessDelivery || ''}
              data-test-id="paperlessDelivery" />
          </div>
        </div>

        {
          /* eslint-disable react/button-has-type */
          <button
            className={primaryButtonClasses}
            type={formValid ? 'submit' : 'button'}
            onClick={onClick} >
             Save and Continue
          </button>
        }
      </div>
    )
  }

}

export default compose(
  makeForm,
  withFormResponseHandler,
)(TrustDetailsForm)



// WEBPACK FOOTER //
// ./src/components/Onboarding/Accounts/TrustDetails/TrustDetailsForm.jsx